import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  Children,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  leadingIcon?: ReactNode;
  prefix?: string;
}

export function Input({
  label,
  error,
  helper,
  leadingIcon,
  prefix,
  className,
  id,
  ...props
}: InputProps) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-[13px] font-medium text-text-primary">
          {label}
          {props.required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted [&>svg]:h-4 [&>svg]:w-4">
            {leadingIcon}
          </span>
        )}
        {prefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-muted">
            {prefix}
          </span>
        )}
        <input
          id={inputId}
          aria-describedby={error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined}
          className={cn(
            'h-10 w-full rounded-lg border bg-bg-surface px-3 text-sm text-text-primary shadow-xs transition-all duration-150',
            'placeholder:text-text-muted',
            'focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/30',
            leadingIcon && 'pl-9',
            prefix && 'pl-7',
            error
              ? 'border-danger focus-visible:border-danger focus-visible:ring-danger/25'
              : 'border-border-strong',
            className,
          )}
          {...props}
        />
      </div>
      {error && (
        <p id={`${inputId}-error`} className="text-xs text-danger">
          {error}
        </p>
      )}
      {helper && !error && (
        <p id={`${inputId}-helper`} className="text-xs text-text-secondary">
          {helper}
        </p>
      )}
    </div>
  );
}

export const selectFieldClasses =
  'flex h-10 w-full items-center justify-between rounded-lg border border-border-strong bg-bg-surface px-3 pr-9 text-left text-sm text-text-primary shadow-xs transition-all duration-150 focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50';

type SelectOption = { value: string; label: string; disabled?: boolean };

function parseSelectOptions(children: ReactNode): SelectOption[] {
  const options: SelectOption[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>(child) && child.type === 'option') {
      options.push({
        value: String(child.props.value ?? ''),
        label: String(child.props.children ?? child.props.value ?? ''),
        disabled: child.props.disabled,
      });
    }
  });
  return options;
}

export function DropdownSelect({
  value,
  onChange,
  options,
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
}: {
  value?: string | number | readonly string[];
  onChange?: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = String(Array.isArray(value) ? value[0] : value ?? '');
  const selected = options.find((o) => o.value === current);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(selectFieldClasses, open && 'border-brand ring-2 ring-brand/30', className)}
      >
        <span className="truncate">{selected?.label ?? 'Select…'}</span>
        <ChevronDown
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-bg-surface py-1 shadow-dropdown animate-fade-in"
        >
          {options.map((opt) => {
            const active = opt.value === current;
            return (
              <li key={opt.value || '__empty'} role="option" aria-selected={active}>
                <button
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm transition-colors',
                    active ? 'bg-brand-subtle font-medium text-brand' : 'text-text-primary hover:bg-bg-subtle',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  helper?: string;
  onChange?: SelectHTMLAttributes<HTMLSelectElement>['onChange'];
}

export function Select({ label, error, helper, className, id, children, value, onChange, disabled, required }: SelectProps) {
  const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
  const options = useMemo(() => parseSelectOptions(children), [children]);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={selectId} className="text-[13px] font-medium text-text-primary">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      <DropdownSelect
        id={selectId}
        value={value}
        disabled={disabled}
        options={options}
        className={cn(error && 'border-danger focus-visible:border-danger focus-visible:ring-danger/25', className)}
        onChange={(next) => {
          onChange?.({ target: { value: next } } as React.ChangeEvent<HTMLSelectElement>);
        }}
      />
      {error && <p className="text-xs text-danger">{error}</p>}
      {helper && !error && <p className="text-xs text-text-secondary">{helper}</p>}
    </div>
  );
}
